import React from 'react';
import SEO from '../components/common/SEO';

const securityPillars = [
  {
    title: 'Private Deployment & Data Sovereignty',
    desc: 'Deploy agents on your own infrastructure (AWS, Azure, GCP), private cloud, or fully air-gapped on-premise hardware. Data never leaves your controlled environment.',
    icon: '🛡️'
  },
  {
    title: 'Policy-as-Code Guardrails',
    desc: 'Define and enforce operational boundaries, ethical guidelines, and regulatory compliance rules programmatically across all agent workflows.',
    icon: '📜'
  },
  {
    title: 'Immutable Audit Logging',
    desc: 'Every model request, tool execution, and agent decision is recorded in detailed, tamper-proof logs for security review and compliance auditing.',
    icon: '📑'
  },
  {
    title: 'Human-in-the-Loop Controls',
    desc: 'Configure mandatory approval gates for high-risk or high-value actions, ensuring AI serves human experts rather than operating in a vacuum.',
    icon: '🤝'
  },
  {
    title: 'PII Redaction & Masking',
    desc: 'Automatically identify and redact sensitive personally identifiable information (PII) before it reaches the model processing layer.',
    icon: '🔒'
  },
  {
    title: 'SOC2 & ISO Ready',
    desc: 'Our platform and methodology are designed to support enterprise security standards, simplifying your path to AI compliance and certification.',
    icon: '✅'
  }
];

export default function SecurityGovernance() {
  const sectionLight = { padding: '80px 0', background: '#f8fafc' };
  const sectionWhite = { padding: '80px 0', background: 'white' };
  const sectionDark = { padding: '80px 0', background: '#0f172a' };
  const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };
  const card = { borderRadius: '20px', border: '1px solid rgba(148,163,184,0.16)', background: 'white', padding: '28px 32px' };

  return (
    <>
      <SEO
        title="Enterprise AI Security & Governance | Eagentix"
        description="Eagentix provides a secure harness for enterprise AI agents, featuring private deployment, policy-as-code guardrails, and immutable audit trails."
        pathname="/security-and-governance"
      />

      {/* Hero */}
      <section style={{ ...sectionDark, paddingTop: '120px' }}>
        <div style={container}>
          <div style={{ maxWidth: '800px' }}>
            <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Trust & Safety
            </span>
            <h1 style={{ fontSize: '3rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', margin: '16px 0 24px' }}>
              Enterprise AI Security & Governance
            </h1>
            <p style={{ fontSize: '1.2rem', color: '#cbd5e1', lineHeight: 1.75, marginBottom: '36px' }}>
              Eagentix provides the deterministic harness required to deploy AI agents safely at scale. From data sovereignty to policy enforcement, we ensure your AI transformation is secure and compliant.
            </p>
          </div>
        </div>
      </section>

      {/* Security Pillars */}
      <section style={sectionLight}>
        <div style={container}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
            {securityPillars.map((pillar, i) => (
              <div key={i} style={card}>
                <div style={{ fontSize: '2rem', marginBottom: '16px' }}>{pillar.icon}</div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>{pillar.title}</h3>
                <p style={{ color: '#475569', lineHeight: 1.7, margin: 0 }}>{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Agent Harness */}
      <section style={sectionWhite}>
        <div style={container}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'center' }}>
            <div>
              <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '24px' }}>
                The Deterministic Harness
              </h2>
              <p style={{ color: '#475569', fontSize: '1.1rem', lineHeight: 1.8, marginBottom: '24px' }}>
                A raw LLM is not an enterprise worker. The Eagentix "Harness" provides the essential layers of control that turn stochastic models into accountable digital agents.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  'Model-agnostic routing with enterprise constraints',
                  'Durable memory and institutional context injection',
                  'Controlled tool execution inside secure sandboxes',
                  'Continuous evaluation and self-correction loops',
                  'Mandatory expert escalation for low-confidence work'
                ].map((item, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', color: '#1d1d1f', fontWeight: 500 }}>
                    <span style={{ color: '#10b981' }}>✓</span> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ background: '#f1f5f9', borderRadius: '24px', padding: '40px', border: '1px solid rgba(0,0,0,0.05)' }}>
              <h4 style={{ color: '#0071e3', textTransform: 'uppercase', fontSize: '0.8rem', letterSpacing: '0.1em', marginBottom: '16px', fontWeight: 700 }}>
                Compliance Mapping
              </h4>
              <p style={{ fontSize: '0.95rem', color: '#475569', lineHeight: 1.6, marginBottom: '20px' }}>
                Eagentix controls map directly to enterprise risk management frameworks:
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ background: 'white', padding: '12px 16px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600 }}>MAS TRM (Singapore)</div>
                <div style={{ background: 'white', padding: '12px 16px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600 }}>GDPR / PDPA Privacy</div>
                <div style={{ background: 'white', padding: '12px 16px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600 }}>SOC2 Type II Controls</div>
                <div style={{ background: 'white', padding: '12px 16px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600 }}>NIST AI Risk Management Framework</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={sectionDark}>
        <div style={{ ...container, textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px' }}>
            Build Securely from Day One
          </h2>
          <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.75, marginBottom: '36px', maxWidth: '600px', margin: '0 auto 36px' }}>
            Don't compromise on security for the sake of speed. Eagentix helps you deploy enterprise AI agents that are governed and safe by design.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="/#contact"
              style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
            >
              Contact Security Team
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
